import EventEmitter from 'events';
import { error, warn } from '@ali/re-utils';
import { uniqueKey, hasOwn } from './utils';
import Form from './form';
import Types from './types';
import MList from './types/list';
import MMap from './types/map';
import MScalar from './types/scalar';

function isScalarType(type) {
  return type === Types.BOOL || type === Types.NUMBER || type === Types.STRING;
}

export default class Atom {
  static create(rawData) {
    if (rawData instanceof Atom) {
      return rawData;
    }
    return new Atom(rawData);
  }

  __MUTABLE = true;

  isRoot = false;

  constructor(data) {
    this.emitter = new EventEmitter;
    this.emitter.setMaxListeners(0);
    this.fromData(data);
    this.id = uniqueKey();
    this.cacheData = null;
    this.updateStat = 0;
    this.lastCached = -1;
    this.subforms = {};
    this.substates = [];
  }

  getId() {
    return this.id;
  }

  getPath() {
    if (this.owner) {
      const key = this.owner.keyOf(this);
      const parentPath = this.owner.getPath();
      if (key === null || parentPath === null) {
        return null;
      }
      if (parentPath) {
        return `${parentPath}/${key}`;
      }
      return key;
    }
    return null;
  }

  keyOf(item) {
    if (this.isIterable()) {
      return this.data.keyOf(item);
    }

    return null;
  }

  /**
   * 从原始数据初始化
   */
  fromData(data) {
    const type = Types.test(data);
    this.type = type;
    switch (type) {
      case Types.BOOL:
      case Types.STRING:
      case Types.NUMBER:
        this.data = new MScalar(data, this, type);
        this.type = Types.SCALAR;
        break;
      case Types.LIST:
        this.data = new MList(data, this);
        break;
      case Types.MAP:
        this.data = new MMap(data, this);
        break;
      case Types.NULL:
      default:
        this.data = null;
        this.type = Types.NULL;
        break;
    }
  }

  /**
   * 创建一个子数据
   */
  create(data) {
    return Atom.create(data).setOwner(this);
  }

  /**
   * 设置归属
   */
  setOwner(owner) {
    if (this.owner === owner) {
      return this;
    }
    if (this.owner) {
      this.owner.removeItem(this);
    }
    this.owner = owner;
    return this;
  }

  /**
   * 获得归属
   */
  getOwner() {
    return this.owner;
  }

  // FIXME: setOwnerForm() 这个点需要注意，setOwnerForm 不应该直接被高级对象通过 atom.ownerForm = xxx 操作，这样会存在隐患
  getOwnerForm() {
    if (this.ownerForm) {
      return this.ownerForm;
    }

    if (this.owner) {
      return this.owner.getOwnerForm();
    }

    return null;
  }

  /**
   * @param {*} formId fieldId/groupId
   */
  getForm(formId) {
    if (!formId) {
      formId = '';
    }

    if (!hasOwn(this.subforms, formId)) {
      this.subforms[formId] = formId === '' && this.ownerForm
        ? this.ownerForm : new Form();
    }

    return this.subforms[formId];
  }

  getFormData(formId) {
    return this.getForm(formId).getData();
  }

  getRoot() {
    if (this.owner) {
      return this.owner.getRoot();
    }
    if (this.ownerScope) {
      return this.ownerScope.getRoot();
    }
    return this;
  }

  /**
   * 是否列表
   */
  isList() {
    return this.getType() === Types.LIST;
  }

  /**
   * 是否映射
   */
  isMap() {
    return this.getType() === Types.MAP;
  }

  /**
   * 是否为空
   */
  isNull() {
    return this.getType() === Types.NULL;
  }

  isIterable() {
    return this.isMap() || this.isList();
  }

  isScalar() {
    return this.getType() === Types.SCALAR;
  }

  /**
   * 是否布尔
   */
  isBool() {
    return this.isScalar() && this.data.isBool();
  }

  /**
   * 是否字符串
   */
  isString() {
    return this.isScalar() && this.data.isString();
  }

  /**
   * 是否数字
   */
  isNumber() {
    return this.isScalar() && this.data.isNumber();
  }

  /**
   * 获得类型
   */
  getType() {
    return this.type;
  }

  /**
   * 获取原始值
   * 对内
   * @param {String|null} key
   */
  getValue(key, force) {
    if (key != null && (this.isIterable())) {
      const ret = this.data.get(key);
      return ret ? ret.toJS(force) : null;
    }
    return this.data ? this.data.toJS(force) : null;
  }

  /**
   * 获取原始值
   * 对外
   * @param {String|null} key
   */
  getData(key, force) {
    this.reportSelfObserved();
    return this.getValue(key, force);
  }

  setVal(value) {
    this.set('fieldData', { value });
  }

  mergeVal(value) {
    this.merge('fieldData', { value });
  }

  getVal() {
    // use getData to report observed
    return this.getData('fieldData/value');
  }

  setBehavior(val) {
    this.set('behavior', { fieldBehavior: val });
    return this;
  }

  getBehavior() {
    return this.getData('behavior/fieldBehavior');
  }


  /**
   * 用于初始化过程，绑定过程不触发 change
   * 一般用户切换父节点的时候使用
   */
  bind(key, state) {
    if (!(state instanceof Atom) || !state.isState) {
      error(`The ${key} you bind must be State`);
      return;
    }

    if (!this.isMap() && !this.isNull()) {
      warn(`Cannot bind state to "${this.type}"`);
      return;
    }

    if (this.isNull()) {
      this.data = new MMap(null, this);
      this.type = Types.MAP;
    }
    // ???? 这一步是干啥，把当前 state 挂到某个属性下？
    this.data.bind(key, state);

    state.setOwner(this);

    // we are going to bind
    this.emitter.emit('bind');

    // FIXME: 父类里面为何去处理子类的逻辑，这个是反模式的
    if (state.has('groupId') || state.isFormField) {
      const form = this.getForm(state.getValue('groupId'));
      form.add(state);
    }
  }

  add(item) {
    this.substates.push(item);
    item.onPurge(() => {
      const i = this.substates.indexOf(item);
      if (i > -1) {
        this.substates.splice(i, 1);
      }
    });
  }

  // method for Map|List
  get(key) {
    if (key == null) {
      return this;
    }

    if (this.isIterable()) {
      return this.data.get(key);
    }
    return null;
  }

  canReceive(data, type) {
    if (!type) {
      type = Types.test(data);
    }

    return this.canReceiveType(type);
  }

  canReceiveType(type) {
    return (
      type === Types.NULL || this.isNull()
      || type === this.getType() || (isScalarType(type) && this.isScalar())
    );
  }

  merge(key, data) {
    this.set(key, data, true);
  }

  set(key, data, merge = false) {
    if (this.getRoot() && this.getRoot().isInBatch()) {
      throw new Error('Please don\'t set values in template.');
    }
    if (this.isNull()) {
      this.data = new MMap(null, this);
      this.type = Types.MAP;
      this.data.set(key, data, merge);
    } else if (this.isIterable()) {
      this.data.set(key, data, merge);
    }
  }

  mergeData(data, targetKeys) {
    this.setData(data, true, targetKeys);
  }

  /**
   * 对atom进行设值操作
   * @param {object} data 需要被设置的值
   * @param {bool} merge 是否合并
   * @param {array} targetKeys 需要被改的key
   * @return {bool} 是否改变了
   */
  setData(data, merge = false, targetKeys = []) {
    if (this.isNull()) {
      this.fromData(data);
      this.statChange();
    } else if (data == null && !merge) {
      const releaseChange = this.changeCapture();
      if (this.isIterable()) {
        this.data.clear();
      }
      if (this.data !== null) {
        this.data = null;
        this.type = Types.NULL;
        this.markChange();
      }
      releaseChange(targetKeys, false);
    } else {
      return this.data.into(data, null, merge, targetKeys);
    }
    return true;
  }

  has(key) {
    if (this.isIterable()) {
      return this.data.has(key);
    }
    return false;
  }

  toJS(force) {
    if (!force && this.lastCached === this.updateStat) {
      return this.cacheData;
    }

    if (this.isNull()) {
      this.cacheData = null;
    } else {
      this.cacheData = this.data.toJS(force);
    }

    this.lastCached = this.updateStat;

    return this.cacheData;
  }

  valueOf() {
    return this.toJS();
  }

  toString() {
    if (this.isNull()) {
      return '';
    }
    return this.data.toString();
  }

  // methods for List
  pop() {
    if (this.isList()) {
      return this.data.pop();
    }
    return null;
  }

  unshift(item) {
    this.splice(0, 0, item);
    return this.size();
  }

  shift() {
    if (this.isList()) {
      return this.data.shift();
    }
    return null;
  }

  push(item) {
    this.splice(this.size(), 0, item);
    return this.size();
  }

  slice(start, end) {
    if (this.isList()) {
      return this.data.slice(start, end);
    }
    return [];
  }

  splice(start, deleteCount, ...items) {
    if (this.isList()) {
      return this.data.splice(start, deleteCount, ...items);
    }
    if (this.isNull()) {
      this.data = new MList(items, this);
      this.type = Types.LIST;
      // 这个地方添加了targetKeys，会强制父节点刷新
      this.statChange([this.getSelfKey()]);
    }
    return [];
  }

  forEach(fn) {
    if (this.isIterable()) {
      this.data.forEach(fn);
    }
  }

  map(fn) {
    if (this.isIterable()) {
      return this.data.map(fn);
    }
    return [];
  }

  every(fn) {
    if (this.isIterable()) {
      return this.data.every(fn);
    }
    return false;
  }

  some(fn) {
    if (this.isIterable()) {
      return this.data.some(fn);
    }
    return false;
  }

  find(fn) {
    if (this.isList()) {
      return this.data.find(fn);
    }
    return null;
  }

  findIndex(fn) {
    if (this.isList()) {
      return this.data.findIndex(fn);
    }
    return -1;
  }

  filter(fn) {
    if (this.isList()) {
      return this.data.filter(fn);
    }
    return [];
  }

  sort(fn) {
    if (this.isList()) {
      return this.data.sort(fn);
    }
    return [];
  }

  get length() {
    return this.size();
  }

  /**
   * size of array|string
   */
  size() {
    if (!this.isNull() && this.data) {
      return this.data.size();
    }
    return 0;
  }

  clear() {
    if (!this.isNull()) {
      this.data.clear();
    }
  }

  /**
   * 递归销毁对象
   */
  purge() {
    if (this.purged) return;
    this.purged = true;
    this.owner = null;
    this.emitter.emit('purge');
    this.emitter.removeAllListeners();
    this.substates.forEach(item => item.purge());
    if (this.isIterable()) {
      this.data.forEach(item => item.purge());
    }
  }

  /**
   * remove from owner of Map | List
   */
  remove() {
    const { owner } = this;
    this.purge();
    if (owner) {
      owner.removeItem(this);
    }
  }

  removeChainFor(entry) {
    this.removeChainForEntry = entry;
  }

  removeItem(item) {
    if (this.removeChainForEntry && this.keyOf(item) === this.removeChainForEntry) {
      this.remove();
      return;
    }
    if (this.isIterable()) {
      this.data.removeItem(item);
    }
  }

  setCurrentTargetDisable(flag) {
    this.currentTargetDisabled = flag;
  }

  isCurrentTargetDisabled() {
    if (this.currentTargetDisabled) {
      return true;
    }

    if (this.owner) {
      return this.owner.isCurrentTargetDisabled();
    }

    return false;
  }

  statChange(targetKeys) {
    this.markChange();
    this.releaseChange(targetKeys);
  }

  markChange() {
    this.updateStat++;
    if (this.owner) {
      this.owner.markChange();
    }
  }

  changeCapture() {
    this.capturing = true;
    if (!this.captures) {
      this.captures = [];
    }
    return (targetKeys) => {
      this.releaseChange(targetKeys, false);
    };
  }

  releaseCapture(targetKeys) {
    if (!this.emitterLock) {
      this.emitterLock = true;
      this.captures.forEach(item => item.emitChange(targetKeys));
      this.emitterLock = false;
    }
    this.capturing = null;
    this.captures = [];
  }

  /**
   *
   * @param {array} targetKeys 变化额key的数组
   * @param {number | undefined} cascadeDone 默认进行4级联动。联动时，会逐级递减这个值
   */
  emitChange(targetKeys, cascadeDone = 3) {
    if (this.emitterDisabled || this.purged) {
      return;
    }
    const fieldId = this.getData('fieldId');
    let emitter;
    const keys = [];
    // componentSchema 标识符说明为组件 State 层
    // FIXME: 这里不应该知晓子类逻辑，需要移动到子类里面处理
    if (this.componentSchema || this.isRoot) {
      ({ emitter } = this);
      keys.push(fieldId);
    }
    if (this.isRoot) {
      this.captureCascade(cascadeDone, targetKeys);
    }
    if (this.owner) {
      if (targetKeys && targetKeys.length) {
        let { owner } = this;
        // 不触发中间环节，直接到顶
        while (owner && !owner.isRoot) {
          const key = owner.getData('fieldId') || owner.getData('$index');
          if (key != null) {
            if (!emitter) {
              // 从来没有发过一次状态变换，至少要发一次
              ({ emitter } = owner);
            }
            keys.push(key);
          }
          ({ owner } = owner);
        }
        keys.reverse();
        if (owner) {
          owner.releaseChange((targetKeys || ['']).map(key => `${keys.join('/')}/${key}`), null);
        }
      }
    }
    if (emitter) {
      emitter.emit('change', targetKeys);
    }
    if (this.isRoot) {
      this.releaseCascade(cascadeDone, targetKeys);
    }
  }

  releaseChange(targetKeys, capturing = null) {
    if (capturing !== null) {
      this.capturing = capturing;
      if (capturing === false) {
        this.releaseCapture(targetKeys);
      }
    }
    if (this.capturing) {
      this.captures.push(this);
      return;
    }
    this.emitChange(targetKeys);
  }

  disableEmitter() {
    if (this.emitterDisabled) {
      return () => {};
    }

    this.emitterDisabled = true;
    return () => {
      this.emitterDisabled = false;
    };
  }

  addTeardown() {
    return null;
  }

  getIteratorId() {
    return this.__iteratorId;
  }

  setIteratorId(id) {
    this.__iteratorId = id;
    return this;
  }

  getIndex() {
    return this.__index;
  }

  setIndex(index) {
    this.__index = index;
    return this;
  }

  /**
   * 在明细和迭代器组件中，设置默认值时使用当前组件的值
   * 和 __mergeAfterBind 的作用一样
   * @param {[bool]} val [true or false] 如果是 false, 则用父级的默认值覆盖子级的
   */
  setUseSelfDefaultValue(val) {
    this.useSelfDefaultValue = val;
  }

  isUseSelfDefaultValue() {
    return this.useSelfDefaultValue;
  }

  /**
   * Events: change
   */
  onChange(fn) {
    this.emitter.on('change', fn);
    return () => {
      this.emitter.removeListener('change', fn);
    };
  }

  /**
   * Events, bind
   * @param {function} fn callback
   */
  onBind(fn) {
    this.emitter.on('bind', fn);
    return () => {
      this.emitter.removeListener('bind', fn);
    };
  }

  onPurge(fn) {
    this.emitter.on('purge', fn);
    return () => {
      this.emitter.removeListener('purge', fn);
    };
  }

  // 获取自己的key
  getSelfKey() {
    let key;
    if (this.owner) {
      Object.keys((this.owner.data && this.owner.data.maps) || {}).forEach((k) => {
        if (this.owner.data.maps[k] === this) {
          key = k;
        }
      });
    }
    return key;
  }

  isConnectedToComponent() {
    return false;
  }

  /**
   * this state might be used in some template
   */
  reportSelfObserved() {
    const store = this.getRoot();
    // this atom has been used, need to check if someone is watching
    if (store && store.isInBatch()) {
      // need to report it's nearest state has been observed
      let owner = this;
      while (!(owner.isConnectedToComponent && owner.isConnectedToComponent())) {
        ({ owner } = owner);
      }
      if (owner) {
        store.reportObserved(owner.getValue('fieldId'), owner.getPath());
      }
    }
  }

  isInBatch() {
    return false;
  }
}
